import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import CanvasJSReact from "@canvasjs/react-charts";
import { ChevronDown, ChevronRight, Flag, Receipt, Target, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { paymentSectionWiseAnalysis } from "../../Action/courseAction";
import {
  CustomAdminTable,
  CustomBadge,
  CustomButton,
  CustomDropdowns,
  CustomPageLayout,
  CustomStatCard,
  useHeaderFilterButton,
} from "../../Component/customComponents";
import {
  TableTitle,
  TotalsModal,
  buildDateFilterFields,
  formatINR,
  todayISO,
} from "../Common/transactionCommon";
import {
  EDUTECH,
  PUBLICATION,
  TRANSACTION_TYPES,
  buildRevenueChartOptions,
  revenueBreakdownRows,
} from "../Common/revenueAnalysisCommon";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

// this report only has Edutech and Publication figures (Shopify was always empty)
const TYPES = TRANSACTION_TYPES.filter((t) => t._id === EDUTECH || t._id === PUBLICATION);

const emptyFilters = () => ({ startDate: todayISO(), endDate: todayISO() });

const num = (v) => Number(v) || 0;
const pct = (part, whole) => (num(part) > 0 && num(whole) ? (num(part) / num(whole)) * 100 : 0);

/** One group (tab) for the selected type */
const groupView = (group, typeId) => {
  const isEdutech = typeId === EDUTECH;
  const groupTotal = isEdutech ? group?.actualTotalAmount : group?.publicationTotalAmount;
  const sections = (group?.sections || []).map((section, s) => {
    const sectionRevenue = isEdutech ? section?.actualTotal : section?.publicationTotal;
    return {
      key: `s-${s}`,
      index: s,
      kind: "section",
      name: section?.sectionName || "Unknown",
      revenue: sectionRevenue,
      gst: isEdutech ? section?.gstTotal : 0,
      total: isEdutech ? section?.edutechTotal : section?.publicationTotal,
      overall: pct(sectionRevenue, groupTotal),
      children: (section?.paymentTypes || []).map((pt, p) => {
        const revenue = isEdutech ? pt?.actualAmount : pt?.publication;
        return {
          key: `s-${s}-p-${p}`,
          kind: "payment",
          name: pt?.name || "Unknown",
          revenue,
          gst: isEdutech ? pt?.gstAmount : 0,
          total: isEdutech ? pt?.edutech : pt?.publication,
          sectionShare: pct(revenue, sectionRevenue),
          overall: pct(revenue, groupTotal),
        };
      }),
    };
  });

  return {
    sections,
    totals: {
      actual: sections.reduce((a, s) => a + (isEdutech ? num(s.revenue) : 0), 0),
      gst: sections.reduce((a, s) => a + num(s.gst), 0),
      publication: isEdutech ? 0 : sections.reduce((a, s) => a + num(s.revenue), 0),
      shopify: 0,
      total: sections.reduce((a, s) => a + num(s.total), 0),
    },
    // chart rows in the API order (as before), in the shape the shared chart builder reads
    chartRows: sections.map((s) => ({
      _id: s.name,
      actualTotalAmount: isEdutech ? s.revenue : 0,
      totalGstAmount: s.gst,
      publication: isEdutech ? 0 : s.revenue,
    })),
  };
};

const PercentCell = ({ value }) => (
  <div>
    <span style={{ fontVariantNumeric: "tabular-nums" }}>{value.toFixed(2)} %</span>
    <div className="progress mt-1" style={{ height: 6 }} aria-hidden="true">
      <div className="progress-bar" style={{ width: `${Math.min(100, value)}%`, background: "var(--cui-primary)" }} />
    </div>
  </div>
);

const PaymentWiseAnalysis = () => {
  const dispatch = useDispatch();
  const headerFilter = useHeaderFilterButton(); // header Filters button → the table's drawer

  const [filters, setFilters] = useState(emptyFilters);
  const [transactionType, setTransactionType] = useState(TYPES[0]); // Edutech, as before
  const [groups, setGroups] = useState([]); // response.data.finalResult (the tabs)
  const [targetData, setTargetData] = useState({});
  const [activeGroupType, setActiveGroupType] = useState("");
  const [expanded, setExpanded] = useState({ 0: true }); // first section open, as before
  const [loading, setLoading] = useState(false);
  const [totalsOpen, setTotalsOpen] = useState(false);

  const typeId = transactionType?._id;

  /* ---------- API ---------- */
  const fetchAnalysis = (f) => {
    setLoading(true);
    dispatch(
      paymentSectionWiseAnalysis(
        { startDate: f.startDate, endDate: f.endDate },
        (response) => {
          const list = response?.data?.finalResult || [];
          setGroups(list);
          setTargetData(response?.data?.targetData || {});
          setActiveGroupType(list[0]?.type ?? "");
          setExpanded({ 0: true });
          setLoading(false);
        },
        () => {
          setGroups([]);
          setTargetData({});
          setLoading(false);
        },
      ),
    );
  };

  useEffect(() => {
    fetchAnalysis(filters); // today, as before
  }, []);

  /* ---------- selected group ---------- */
  const activeGroup = groups.find((g) => g?.type === activeGroupType) || groups[0];
  const view = useMemo(() => groupView(activeGroup, typeId), [activeGroup, typeId]);
  const headline = typeId === EDUTECH ? view.totals.actual : view.totals.publication;

  const chartOptions = useMemo(
    () =>
      buildRevenueChartOptions({
        rows: view.chartRows,
        typeId,
        title: "Payment Wise Analysis",
        axisXTitle: "Payment Type",
      }),
    [view, typeId],
  );

  // section rows, each followed by its payment types when open
  const tableRows = view.sections.flatMap((section) =>
    expanded[section.index] ? [section, ...section.children] : [section],
  );
  const toggle = (index) => setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));

  const money = (v) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatINR(v)}</span>;

  const columns = [
    {
      key: "name",
      header: "Source",
      minWidth: 200,
      render: (v, row) =>
        row.kind === "section" ? (
          <button
            type="button"
            className="btn btn-link p-0 text-reset text-decoration-none fw-bold d-inline-flex align-items-center gap-1"
            onClick={() => toggle(row.index)}
            aria-expanded={Boolean(expanded[row.index])}
          >
            {expanded[row.index] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            {v}
            <CustomBadge tone="neutral" size="sm">
              {row.children.length}
            </CustomBadge>
          </button>
        ) : (
          <span className="ps-4 d-inline-block">{v}</span>
        ),
    },
    {
      key: "revenue",
      header: typeId === EDUTECH ? "Edutech Actual Revenue" : "Publication Revenue",
      align: "right",
      render: (v, row) => (row.kind === "section" ? <strong>{money(v)}</strong> : money(v)),
    },
    { key: "gst", header: "GST Amount", align: "right", hidden: typeId !== EDUTECH, render: money },
    { key: "total", header: "Total Revenue", align: "right", render: money },
    {
      key: "sectionShare",
      header: "Section Wise Contribution",
      minWidth: 150,
      render: (v, row) => (row.kind === "section" ? "—" : <PercentCell value={v} />),
    },
    { key: "overall", header: "Overall Contribution", minWidth: 150, render: (v) => <PercentCell value={v} /> },
  ];

  const achievedOk = num(targetData?.achieved) >= num(targetData?.target);

  return (
    <CustomPageLayout title="PAYMENT WISE ANALYSIS" headerActions={headerFilter.button}>
      <div className="d-flex flex-column gap-3">
        {/* ---------- groups (old tabs): click to switch ---------- */}
        {groups.length > 0 && (
          <div className="row g-3">
            {groups.map((group, i) => (
              <CustomStatCard
                key={`${group?.type}-${i}`}
                colClass="col-lg-3 col-sm-6 col-12"
                title={group?.type || "—"}
                value={formatINR(typeId === EDUTECH ? group?.actualTotalAmount : group?.publicationTotalAmount)}
                subtitle={`${num(
                  typeId === EDUTECH ? group?.overallEdutechContribution : group?.overallPublicationContribution,
                )}% of overall`}
                icon={<Wallet size={18} />}
                tone="primary"
                active={group?.type === activeGroup?.type}
                loading={loading}
                onClick={() => {
                  setActiveGroupType(group?.type);
                  setExpanded({ 0: true });
                }}
              />
            ))}
          </div>
        )}

        {/* ---------- target ---------- */}
        <div className="row g-3">
          <CustomStatCard
            colClass="col-lg col-sm-6 col-12"
            title="Target"
            value={formatINR(targetData?.target)}
            icon={<Target size={18} />}
            tone="info"
            loading={loading}
          />
          <CustomStatCard
            colClass="col-lg col-sm-6 col-12"
            title="Achieved"
            value={formatINR(targetData?.achieved)}
            icon={<Flag size={18} />}
            tone={achievedOk ? "success" : "danger"}
            loading={loading}
          />
          <CustomStatCard
            colClass="col-lg col-sm-6 col-12"
            title="Percent Achieved"
            value={`${num(targetData?.percentAchieved)}%`}
            icon={<TrendingUp size={18} />}
            tone="neutral"
            loading={loading}
          />
          <CustomStatCard
            colClass="col-lg col-sm-6 col-12"
            title="Deficit"
            value={formatINR(targetData?.deficite)}
            icon={<TrendingDown size={18} />}
            tone={achievedOk ? "success" : "danger"}
            loading={loading}
          />
          <CustomStatCard
            colClass="col-lg col-sm-6 col-12"
            title="Percent Deficit"
            value={`${num(targetData?.percentDeficit)}%`}
            icon={<TrendingDown size={18} />}
            tone="neutral"
            loading={loading}
          />
        </div>

        {/* ---------- chart (same CanvasJS column chart as before) ---------- */}
        <section className="bg-white border rounded-3 shadow-sm p-3">
          {view.chartRows.length > 0 ? (
            <CanvasJSChart options={chartOptions} />
          ) : (
            <div className="text-center text-muted py-5">
              {loading ? "Loading chart…" : "No revenue for this period."}
            </div>
          )}
        </section>

        {/* ---------- sections → payment types ---------- */}
        <CustomAdminTable
          {...headerFilter.tableProps}
          enableSerialNumber
          serialHeader="S.No"
          id="payment-analysis-table"
          title={
            <TableTitle
              title={`${activeGroup?.type || "Payment"} · ${transactionType?.name} (${view.sections.length} sections)`}
              from={filters.startDate}
              to={filters.endDate}
            />
          }
          columns={columns}
          data={tableRows}
          loading={loading}
          rowKey={(row) => row.key}
          toolbarActions={
            <>
              <div style={{ minWidth: 170 }}>
                <CustomDropdowns
                  placeholder="Transaction Type"
                  options={TYPES}
                  value={transactionType}
                  onChange={(option) => option && setTransactionType(option)}
                  search={false}
                  clearable={false}
                />
              </div>
              <CustomButton
                variant="secondary"
                icon={<Receipt size={15} />}
                onClick={() => setTotalsOpen(true)}
                title="View GST breakdown"
              >
                Total: {formatINR(headline)}
              </CustomButton>
            </>
          }
          advancedFilters={buildDateFilterFields()}
          advancedFilterValues={filters}
          onApplyFilters={(values) => {
            setFilters(values);
            fetchAnalysis(values);
          }}
          applyFiltersOnReset
          filterSubtitle="Pick the date range"
          emptyTitle="No revenue"
          emptyDescription="No payment data for this period."
          maxHeight="70vh"
        />
      </div>

      <TotalsModal
        open={totalsOpen}
        onClose={() => setTotalsOpen(false)}
        subtitle={`${transactionType?.name} · ${activeGroup?.type || "payment wise"}`}
        rows={revenueBreakdownRows(view.totals, typeId)}
      />
    </CustomPageLayout>
  );
};

export default PaymentWiseAnalysis;
